import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppNavigate, usePwaNav } from '../contexts/PwaNavContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion, AnimatePresence } from 'motion/react';
import { ClipboardList, ChefHat, CheckCircle, Search, Frown, Check, UtensilsCrossed, CheckCircle2, X, Sparkles, Star, Gift, ArrowRight, Bell, Wifi, Plus, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import { API_URL } from '../utils/api';
import { track } from '../analytics';
import CustomizationModal from '../components/CustomizationModal';
import FirebaseAuthModal from '../components/FirebaseAuthModal';
import { SkeletonBlock } from '../components/Skeleton';
import { useCustomerAuth, type BadgePayload } from '../contexts/CustomerAuthContext';
import { useRewardNotifications } from '../hooks/useRewardNotifications';
import { RewardPopup, type AnyRewardNotification } from '../components/RewardPopup';
import CustomerUserMenu from '../components/CustomerUserMenu';
import GiftReceivedModal from '../components/GiftReceivedModal';
import OrderOnboarding from '../components/OrderOnboarding';
import NotificationPrompt from '../components/NotificationPrompt';
import { useOrderSocket } from '../hooks/useOrderSocket';
import { RefundSheet } from '../components/RefundSheet';
import { useUpsellCart } from '../hooks/useUpsellCart';
import { usePushPermission } from '../hooks/usePushPermission';
import type { UpsellItem, ServedOffer, ReviewSubmitItem, ReviewSubmitResponse } from '../types/order';
import RatingSheet from '../components/RatingSheet';
import './OrderStatus.css';


function triggerReadyEffect() {
  if ('vibrate' in navigator) {
    navigator.vibrate([100, 80, 200, 80, 200]);
  }
  import('canvas-confetti').then(({ default: confetti }) => {
    const defaults = {
      origin: { y: 0.75 },
      colors: ['#FF5A00', '#F59E0B', '#FCD34D', '#FF8C42', '#FAF7F2', '#ffffff'],
      zIndex: 9999,
    };
    confetti({ ...defaults, particleCount: 60, angle: 60, spread: 55, origin: { x: 0, y: 0.75 } });
    setTimeout(() => {
      confetti({ ...defaults, particleCount: 60, angle: 120, spread: 55, origin: { x: 1, y: 0.75 } });
    }, 150);
  });
}

function triggerPreparingEffect() {
  if ('vibrate' in navigator) {
    navigator.vibrate(60);
  }
}

// triggerReadyEffect and triggerPreparingEffect are exported for use in hooks
export { triggerReadyEffect, triggerPreparingEffect };

function sliceUpsells(
  recommendations: { r1: UpsellItem[] | null; r2: UpsellItem[] | null; v1: UpsellItem[] | null; v2: UpsellItem[] | null } | null,
  tagBasedR1: boolean
): { ownCards: UpsellItem[]; venueCards: UpsellItem[] } {
  if (!recommendations) return { ownCards: [], venueCards: [] };
  const r1 = recommendations.r1 ?? [];
  const r2 = recommendations.r2 ?? [];
  const v2 = recommendations.v2 ?? [];

  const primary = tagBasedR1 ? r1 : r2;
  const secondary = tagBasedR1 ? r2 : r1;
  const seenProductIds = new Set<string>();
  const ownCards: UpsellItem[] = [];
  for (const item of [...primary, ...secondary]) {
    if (ownCards.length >= 4) break;
    if (!seenProductIds.has(item.product.id)) {
      seenProductIds.add(item.product.id);
      ownCards.push(item);
    }
  }

  const venueCards: UpsellItem[] = [];
  for (const item of v2) {
    if (venueCards.length >= 2) break;
    if (!seenProductIds.has(item.product.id)) {
      seenProductIds.add(item.product.id);
      venueCards.push(item);
    }
  }

  return { ownCards, venueCards };
}

function SkeletonPage() {
  return (
    <div className="order-status-container" aria-hidden>
      <div className="os-root">
        {/* Header */}
        <header className="os-header">
          <SkeletonBlock width="36px" height="36px" radius="50%" />
          <div className="os-header-left" style={{ justifyContent: 'flex-start' }}>
            <SkeletonBlock width="36px" height="36px" radius="10px" />
            <div className="os-header-text" style={{ gap: 5 }}>
              <SkeletonBlock width="120px" height="13px" radius="4px" />
              <SkeletonBlock width="70px" height="10px" radius="4px" />
            </div>
          </div>
          <SkeletonBlock width="36px" height="36px" radius="50%" />
        </header>

        {/* Tabs */}
        <div className="os-tabs">
          <SkeletonBlock width="100%" height="44px" radius="12px" />
          <SkeletonBlock width="100%" height="44px" radius="12px" />
        </div>

        {/* Hero — amber tint, same frame as the real card */}
        <section className="os-hero os-hero--skel">
          <div className="os-hero-topRow">
            <div>
              <SkeletonBlock width="46px" height="11px" radius="3px" />
              <SkeletonBlock width="92px" height="34px" radius="6px" style={{ marginTop: 4 }} />
            </div>
            <SkeletonBlock width="86px" height="26px" radius="99px" />
          </div>

          <div className="os-hero-body">
            <SkeletonBlock width="64px" height="64px" radius="20px" />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
              <SkeletonBlock width="62%" height="22px" radius="5px" />
              <SkeletonBlock width="80%" height="13px" radius="4px" />
            </div>
          </div>

          <div className="os-progress">
            <SkeletonBlock width="100%" height="4px" radius="4px" style={{ flex: 1 }} />
            <SkeletonBlock width="100%" height="4px" radius="4px" style={{ flex: 1 }} />
            <SkeletonBlock width="100%" height="4px" radius="4px" style={{ flex: 1 }} />
            <SkeletonBlock width="100%" height="4px" radius="4px" style={{ flex: 1 }} />
          </div>
          <div className="os-progress-labels" style={{ marginTop: 8 }}>
            <SkeletonBlock width="44px" height="9px" radius="3px" />
            <SkeletonBlock width="50px" height="9px" radius="3px" />
            <SkeletonBlock width="42px" height="9px" radius="3px" />
            <SkeletonBlock width="46px" height="9px" radius="3px" />
          </div>
        </section>

        {/* Items summary card */}
        <div className="os-items" style={{ pointerEvents: 'none' }}>
          <div className="os-items-summary">
            <div className="os-items-summary-left">
              <SkeletonBlock width="62px" height="11px" radius="3px" />
              <SkeletonBlock width="34px" height="18px" radius="99px" />
            </div>
            <div className="os-items-summary-right">
              <SkeletonBlock width="58px" height="14px" radius="4px" />
              <SkeletonBlock width="16px" height="16px" radius="4px" />
            </div>
          </div>
        </div>

        {/* Upsell section */}
        <div style={{ margin: '16px 16px 0' }}>
          <SkeletonBlock width="58%" height="14px" radius="4px" />
          <SkeletonBlock width="40%" height="11px" radius="4px" style={{ marginTop: 6 }} />
        </div>
        <div className="os-upsell-grid" style={{ marginTop: 12 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="os-upsell-card skeleton-card">
              <div className="os-upsell-media">
                <div className="skeleton-block" style={{ width: '100%', height: '100%' }} />
              </div>
              <div className="os-upsell-body" style={{ gap: '8px' }}>
                <SkeletonBlock width="90%" height="14px" radius="4px" />
                <SkeletonBlock width="60%" height="14px" radius="4px" />
                <SkeletonBlock width="40%" height="16px" radius="4px" style={{ marginTop: 4 }} />
                <SkeletonBlock width="100%" height="36px" radius="10px" style={{ marginTop: 4 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DebugPanel({
  order,
  debugInfo,
  apiUrl,
  onRefresh,
}: {
  order: NonNullable<ReturnType<typeof useOrderSocket>['order']>;
  debugInfo: ReturnType<typeof useOrderSocket>['debugInfo'];
  apiUrl: string;
  onRefresh: () => void;
}) {
  const [productsDirect, setProductsDirect] = useState<{ name: string; upsellPriority: number }[] | null>(null);
  const [pushLog, setPushLog] = useState<string | null>(null);
  const [swRegs, setSwRegs] = useState<string[]>([]);
  const [backendPushStatus, setBackendPushStatus] = useState<string | null>(null);
  const upsells = order?.upsells || [];
  const rid = order?.restaurantId || order?.restaurant?.id;

  // Read SW registrations on mount
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.getRegistrations().then((regs) => {
      setSwRegs(regs.map((r) => r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || '(no script)'));
    });
  }, []);

  const fetchProductsDirect = async () => {
    if (!rid) return;
    const base = apiUrl || '';
    try {
      const { data } = await axios.get(`${base}/api/products/restaurant/${rid}?_=${Date.now()}`);
      setProductsDirect(
        (data || []).map((p: { name: string; upsellPriority?: number }) => ({
          name: p.name,
          upsellPriority: p.upsellPriority ?? 0,
        }))
      );
    } catch (_e) {
      setProductsDirect([]);
    }
  };

  const testPush = async () => {
    setPushLog('Requesting…');
    try {
      const { requestPushPermission: req } = await import('../utils/pushManager');
      const ok = await req(order.id);
      setPushLog(ok ? 'Token registered OK' : 'Failed — check console');
      // Refresh SW list
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        setSwRegs(regs.map((r) => r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || '(no script)'));
      }
    } catch (e: any) {
      setPushLog('Error: ' + (e?.message || String(e)));
    }
  };

  const checkBackendPush = async () => {
    setBackendPushStatus('Checking…');
    try {
      const base = apiUrl || '';
      const { data } = await axios.get(`${base}/api/orders/push-debug`);
      setBackendPushStatus(`${data.note} | Project: ${data.firebaseProjectId}${data.adminError ? ' | Err: ' + data.adminError : ''}`);
    } catch (e: any) {
      setBackendPushStatus('Request failed: ' + (e?.message || String(e)));
    }
  };

  const notifPermission = typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';

  return (
    <div className="debug-panel">
      <div className="debug-panel-header"> Debug (add ?debug=1 to URL)</div>
      <div className="debug-panel-body">
        <div className="debug-row">
          <strong>Order ID:</strong> {order.id}
        </div>
        <div className="debug-row">
          <strong>Restaurant:</strong> {order.restaurant?.name || '—'} (ID: {order.restaurantId || order.restaurant?.id || '—'})
        </div>
        <div className="debug-row">
          <strong>API base:</strong> {apiUrl || '(empty, using proxy)'}
        </div>
        <div className="debug-row">
          <strong>Last fetch:</strong> {debugInfo?.lastFetch || '—'}
        </div>

        {/* ── Push Notifications ── */}
        <div className="debug-row" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 8, marginTop: 4 }}>
          <strong> Push Notifications</strong>
        </div>
        <div className="debug-row">
          <strong>Permission:</strong>{' '}
          <span className={notifPermission === 'granted' ? '' : 'debug-warn'}>{notifPermission}</span>
        </div>
        <div className="debug-row">
          <strong>FCM token in DB:</strong>{' '}
          {order.fcmToken
            ? <span style={{ color: 'var(--a-green-bright)' }}> {order.fcmToken.slice(0, 20)}…</span>
            : <span className="debug-warn"> None (not registered)</span>}
        </div>
        <div className="debug-row">
          <strong>Service workers:</strong>
          {swRegs.length === 0
            ? <span className="debug-warn"> None</span>
            : <ul className="debug-list">{swRegs.map((s, i) => <li key={i}>{s.split('/').pop()}</li>)}</ul>}
        </div>
        {pushLog && (
          <div className="debug-row">
            <strong>Push test:</strong> {pushLog}
          </div>
        )}
        {backendPushStatus && (
          <div className="debug-row">
            <strong>Backend:</strong> <span style={{ fontSize: 11 }}>{backendPushStatus}</span>
          </div>
        )}
        <div className="debug-actions">
          <button type="button" className="debug-btn" onClick={testPush}>
            Re-register push
          </button>
          <button type="button" className="debug-btn" onClick={checkBackendPush}>
            Backend push status
          </button>
        </div>

        {/* ── Upsells ── */}
        <div className="debug-row" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 8, marginTop: 4 }}>
          <strong>Displayed upsells ({upsells.length})</strong>
          {debugInfo?.upsellsSource && (
            <span className="debug-muted"> — source: {debugInfo.upsellsSource === 'products' ? 'Products API ' : 'order fallback'}</span>
          )}
          {upsells.length === 0 && debugInfo?.productsTotal != null && (
            <span className="debug-warn" style={{ display: 'block', marginTop: 4 }}>
              Products: {debugInfo.productsTotal} total, {debugInfo.productsWithPriority ?? 0} with priority&gt;0, {debugInfo.productsPassedFilter ?? 0} passed filter (active, in stock, not in order)
            </span>
          )}
          {upsells.length === 0 && debugInfo?.productsTotal == null && (
            <span className="debug-warn"> None</span>
          )}
          {upsells.length > 0 && (
            <ul className="debug-list">
              {upsells.map((u) => (
                <li key={u.id}>{u.product.name} (id: {u.id.slice(0, 8)}…)</li>
              ))}
            </ul>
          )}
        </div>
        <div className="debug-row">
          <strong>Product priorities in DB:</strong>
          {debugInfo?.productPriorities ? (
            <ul className="debug-list">
              {debugInfo.productPriorities.map((p: { name: string; upsellPriority: number }) => (
                <li key={p.name}>
                  {p.name}: {p.upsellPriority} {p.upsellPriority > 0 ? '' : ''}
                </li>
              ))}
            </ul>
          ) : (
            <span className="debug-muted"> Click Fetch to load</span>
          )}
        </div>
        <div className="debug-row">
          <strong>Products API (direct):</strong>
          {productsDirect ? (
            <>
              <ul className="debug-list">
                {productsDirect.filter((p) => p.upsellPriority > 0).map((p) => (
                  <li key={p.name}>{p.name} (priority {p.upsellPriority}) </li>
                ))}
                {productsDirect.filter((p) => p.upsellPriority > 0).length === 0 && (
                  <li className="debug-warn">No products with upsellPriority &gt; 0</li>
                )}
              </ul>
              {(() => {
                const orderNames = new Set(upsells.map((u) => u.product.name));
                const directNames = new Set(productsDirect.filter((p) => p.upsellPriority > 0).map((p) => p.name));
                const hasUnexpected = [...orderNames].some((n) => !directNames.has(n));
                const isSubset = orderNames.size < directNames.size && [...orderNames].every((n) => directNames.has(n));
                if (hasUnexpected) {
                  return (
                    <div className="debug-warn" style={{ marginTop: 8 }}>
                       Displayed upsells include items not in Products API. Same backend?
                    </div>
                  );
                }
                if (isSubset) {
                  return (
                    <div className="debug-muted" style={{ marginTop: 8, fontSize: 12 }}>
                      Displayed is a subset of Products API
                    </div>
                  );
                }
                return null;
              })()}
            </>
          ) : (
            <span className="debug-muted"> Click &quot;Products API&quot; to compare</span>
          )}
        </div>
        <div className="debug-actions">
          <button type="button" className="debug-btn" onClick={onRefresh}>
            Refresh order
          </button>
          <button type="button" className="debug-btn" onClick={() => { onRefresh(); fetchProductsDirect(); }}>
            Fetch product priorities
          </button>
          <button type="button" className="debug-btn" onClick={fetchProductsDirect}>
            Products API
          </button>
        </div>
      </div>
    </div>
  );
}


function OrderStatus() {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const { initialQrCode, navData } = usePwaNav();
  const { qrCode: routeQrCode } = useParams<{ qrCode: string }>();
  // navData.qrCode takes priority — in standalone PWA the URL doesn't change,
  // so routeQrCode can be stale from the original launch URL
  const rawQrCode = navData.qrCode || routeQrCode || initialQrCode || undefined;
  const [searchParams] = useSearchParams();
  const debugMode = searchParams.get('debug') === '1';
  const { isAuthenticated, token: customerToken } = useCustomerAuth();

  // Handle ~TOKEN in qrCode (iOS PWA launch format: /order/CODE~TOKEN)
  const qrCode = rawQrCode?.split('~')[0];

  const isStandalone = useMemo(() => {
    const standaloneNav = (window.navigator as { standalone?: boolean }).standalone === true;
    const standaloneMedia = window.matchMedia('(display-mode: standalone)').matches;
    return standaloneNav || standaloneMedia;
  }, []);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authTriggeredByPassport, setAuthTriggeredByPassport] = useState(false);

  const addDebug = useCallback((_msg: string) => { /* debug panel removed */ }, []);

  const [toast, setToast] = useState<{ msg: string; type: 'error' | 'success' } | null>(null);
  const [giftModal, setGiftModal] = useState<{ senderName: string; productName: string } | null>(null);
  const [socialProofDismissed, setSocialProofDismissed] = useState(false);
  const [repeatDismissed, setRepeatDismissed] = useState(false);
  const [popOpenId, setPopOpenId] = useState<string | null>(null);
  const [badgeNotifs, setBadgeNotifs] = useState<BadgePayload[]>([]);
  const [itemsExpanded, setItemsExpanded] = useState(false);
  const [ratingOpen, setRatingOpen] = useState(false);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [ratingDone, setRatingDone] = useState(false);

  const [onboardingDone, setOnboardingDone] = useState(() => {
    try {
      return !!localStorage.getItem('olso-onboarding-done') || !!localStorage.getItem('olso_onboarding_done');
    } catch { return true; }
  });

  const showToast = useCallback((msg: string, type: 'error' | 'success' = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4500);
  }, []);

  // ── useOrderSocket hook ──
  const {
    order,
    loading,
    loadError,
    isOffline,
    upsellsLoading,
    debugInfo,
    statusChanged,
    showReadyFlash,
    setShowReadyFlash,
    showPreparingModal,
    setShowPreparingModal,
    repeatItems,
    socialProofCount,
    venueOrders,
    venueDetailId,
    setVenueDetailId,
    venueDetailData,
    venueDetailLoading,
    handleRefresh,
    openVenueDetail,
    showRefundSheet,
    setShowRefundSheet,
    refundAmount,
  } = useOrderSocket(qrCode, {
    debugMode,
    isAuthenticated,
    navigate,
    addDebug,
    searchParams,
    onPaymentConfirmed: (_data) => {
      try { sessionStorage.removeItem('olso_pending_invoice'); } catch { /* ignore */ }
      setPaymentProcessing(false);
      setCartItems([]);
    },
    onPaymentFailed: (reason) => {
      setPaymentProcessing(false);
      showToast(reason || t('payment.failed'), 'error');
    },
  });

  const submitRating = useCallback(async (payloadItems: ReviewSubmitItem[]) => {
    if (!order) return;
    setRatingSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(customerToken ? { Authorization: `Bearer ${customerToken}` } : {}),
        },
        body: JSON.stringify({ items: payloadItems }),
      });
      if (!res.ok) throw new Error('review failed');
      const data = (await res.json()) as ReviewSubmitResponse;
      setRatingOpen(false);
      setRatingDone(true);
      if (data.bonusPointsAwarded > 0) {
        showToast(t('orderStatus.rateBonusToast', '+50 баллов за отзыв'), 'success');
      } else {
        showToast(t('orderStatus.rateSavedToast', 'Спасибо! Оценка сохранена'), 'success');
      }
    } catch {
      showToast(t('orderStatus.rateError', 'Не удалось отправить оценку'), 'error');
    } finally {
      setRatingSubmitting(false);
    }
  }, [order, customerToken, showToast, t]);

  // ── useUpsellCart hook ──
  const {
    cartItems,
    setCartItems,
    customizeUpsell,
    setCustomizeUpsell,
    visible,
    total,
    addToCart,
    removeFromCart,
    changeQty,
    getCartItemsForProduct,
    getTotalQtyForProduct,
    getUnitPrice,
    formatCustomization,
    hasOptions,
    updateCartItemCustomization,
    menuProducts,
    menuLoading,
    menuError,
    menuSearchQuery,
    setMenuSearchQuery,
    menuActiveCategory,
    setMenuActiveCategory,
    menuByCategory,
    menuCategories,
    fetchMenu,
    activeTab,
    setActiveTab,
  } = useUpsellCart({ order, upsellsLoading });

  // ── Analytics: qr scan + first order view ──
  useEffect(() => {
    if (qrCode) track('qr_scanned', { qrCode });
  }, [qrCode]);

  const viewedOrderRef = useRef<string | null>(null);
  useEffect(() => {
    if (order?.id && viewedOrderRef.current !== order.id) {
      viewedOrderRef.current = order.id;
      track('order_viewed', {
        orderId: order.id,
        restaurantId: order.restaurantId || order.restaurant?.id,
        status: order.status,
        itemCount: order.items?.length,
        total: order.totalAmount,
      });
    }
  }, [order?.id, order?.status, order?.restaurantId, order?.restaurant?.id, order?.items, order?.totalAmount]);

  // ── Analytics: upsell_shown (fire once per upsell.id per mount) ──
  const shownUpsellsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!order?.id || !visible?.length) return;
    for (const u of visible) {
      if (!shownUpsellsRef.current.has(u.id)) {
        shownUpsellsRef.current.add(u.id);
        track('upsell_shown', {
          orderId: order.id,
          restaurantId: order.restaurantId || order.restaurant?.id,
          upsellId: u.id,
          productId: u.product?.id,
          productName: u.product?.name,
          category: u.product?.category,
        });
      }
    }
  }, [visible, order?.id, order?.restaurantId, order?.restaurant?.id]);

  // Close popularity popup on outside tap
  useEffect(() => {
    if (!popOpenId) return;
    const close = () => setPopOpenId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [popOpenId]);

  // ── Payment placeholder (integration in progress — see ApiPay.kz) ──
  // When VITE_PAYMENT_DISABLED=true, the "pay" action bypasses the gateway
  // and pushes cart items straight into the order via /add-upsell. Useful for
  // demos and pre-payment-integration QA. Real Halyk flow takes over when the flag is off.
  const processing = false;
  const paymentProcessing = false;
  const setPaymentProcessing = (_: boolean) => { /* no-op until payment provider wired */ };
  const paymentDisabled = import.meta.env.VITE_PAYMENT_DISABLED === 'true';
  const pay = async () => {
    if (!paymentDisabled) {
      showToast(t('payment.unavailable', 'Оплата временно недоступна'), 'error');
      return;
    }
    if (!order?.id || cartItems.length === 0) return;
    try {
      for (const item of cartItems) {
        await axios.post(
          `${API_URL || ''}/api/orders/${order.id}/add-upsell`,
          {
            productId: item.productId,
            quantity: item.quantity,
            customization: item.customization,
          },
        );
      }
      setCartItems([]);
      showToast(t('payment.testModeAdded', 'Добавлено к заказу (тест-режим)'), 'success');
    } catch (err) {
      showToast((err as Error).message || t('payment.failed', 'Не удалось добавить'), 'error');
    }
  };

  const [applyingOfferId, setApplyingOfferId] = useState<string | null>(null);
  const [appliedOfferIds, setAppliedOfferIds] = useState<string[]>([]);

  const applyOffer = useCallback(async (offer: ServedOffer) => {
    if (!order?.id || applyingOfferId) return;
    setApplyingOfferId(offer.id);
    try {
      await axios.post(`${API_URL || ''}/api/orders/${order.id}/apply-offer`, { offerId: offer.id });
      setAppliedOfferIds((prev) => [...prev, offer.id]);
      track('upsell_offer_applied', { offerId: offer.id, type: offer.type, bundlePrice: offer.bundlePrice });
      // order-updated socket will refresh the order; no manual refetch needed.
    } catch (e) {
      console.error('apply-offer failed', e);
    } finally {
      setApplyingOfferId(null);
    }
  }, [order?.id, applyingOfferId]);

  // ── usePushPermission hook ──
  usePushPermission({
    orderId: order?.id,
    orderStatus: order?.status,
    addDebug,
  });

  // Onboarding refs
  const statusBarRef = useRef<HTMLDivElement>(null);
  const ownUpsellsRef = useRef<HTMLDivElement>(null);
  const venueUpsellsRef = useRef<HTMLDivElement>(null);
  const menuTabRef = useRef<HTMLButtonElement>(null);
  const passportRef = useRef<HTMLButtonElement>(null);

  const [heroOutOfView, setHeroOutOfView] = useState(false);
  useEffect(() => {
    const el = statusBarRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setHeroOutOfView(!entry.isIntersecting),
      { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [statusBarRef.current]);

  // Hide auth modal when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      setShowAuthModal(false);
    }
  }, [isAuthenticated]);

  // Auto-claim unclaimed order when authenticated customer opens it
  const claimedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isAuthenticated || !customerToken || !qrCode) return;
    if (claimedRef.current === qrCode) return;
    claimedRef.current = qrCode;
    const base = API_URL || '';
    axios.post(
      `${base}/api/orders/claim`,
      { qrCode },
      { headers: { Authorization: `Bearer ${customerToken}` } }
    ).catch(() => {
      // Order might already be claimed by someone else, or not exist — ignore
      claimedRef.current = null;
    });
  }, [isAuthenticated, customerToken, qrCode]);

  const { computedHasOwn, computedHasVenue } = useMemo(() => {
    if (!order?.recommendations) return { computedHasOwn: false, computedHasVenue: false };
    const isReady = order?.status === 'READY';
    const tagBasedR1 = order?.restaurant?.preferences?.tagBasedR1 !== false;
    const { ownCards, venueCards } = sliceUpsells(order.recommendations, tagBasedR1);
    return {
      computedHasOwn: ownCards.length > 0,
      computedHasVenue: !isReady && venueCards.length > 0,
    };
  }, [order?.recommendations, order?.restaurant?.preferences?.tagBasedR1, order?.status]);

  const { current: achievementNotif, dismiss: dismissAchievement, queueLength: achQueueLen } = useRewardNotifications();
  // suppress unused var warning
  void achQueueLen;
  const allNotifs: AnyRewardNotification[] = [
    ...badgeNotifs.map((b): AnyRewardNotification => ({
      type: 'badge',
      id: b.id,
      badgeName: b.badgeName,
      badgeIcon: b.badgeIcon,
      currentTierName: b.currentTierName,
      currentTierImage: b.currentTierImage,
      visitCount: b.visitCount,
      restaurantName: b.restaurantName,
    })),
    ...(achievementNotif ? [achievementNotif] : []),
  ];
  const currentNotif = allNotifs[0] ?? null;
  const dismissCurrent = () => {
    if (badgeNotifs.length > 0) setBadgeNotifs((q) => q.slice(1));
    else dismissAchievement();
  };

  if (loading) {
    return <SkeletonPage />;
  }

  if (loadError) {
    return (
      <div className="order-status-container">
        <div className="os-error-state">
          <div className="os-error-icon">
            <Frown size={46} strokeWidth={1.6} />
          </div>
          <div className="os-error-title">{t('orderStatus.loadErrorTitle')}</div>
          <div className="os-error-sub">{loadError}</div>
          <button onClick={() => window.location.reload()} className="retry-btn">
            {t('orderStatus.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-status-container">
        <div className="os-error-state">
          <div className="os-error-icon">
            <Frown size={46} strokeWidth={1.6} />
          </div>
          <div className="os-error-title">{t('orderStatus.orderNotFound')}</div>
          <div className="os-error-sub">{t('orderStatus.notFoundSub', 'Проверьте QR-код на чеке или попросите сотрудника повторно сканировать')}</div>
          <button onClick={() => navigate('/')} className="retry-btn">
            {t('orderStatus.scanAgain', 'Отсканировать ещё раз')}
          </button>
        </div>
      </div>
    );
  }

  const statusLabels: Record<string, string> = {
    PENDING: t('orderStatus.inQueue'),
    PREPARING: t('orderStatus.preparing'),
    READY: t('orderStatus.ready'),
    COMPLETED: t('orderStatus.completed'),
    CANCELLED: t('orderStatus.cancelled'),
  };
  const status = statusLabels[order.status] || t('orderStatus.unknownStatus');

  const handlePassportClick = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!isAuthenticated) {
      setAuthTriggeredByPassport(true);
      setShowAuthModal(true);
      return;
    }
    navigate('/passport');
  };

  const tintFor = (cat?: string): string => {
    const c = (cat || '').toLowerCase().trim();
    if (c.includes('drink') || c.includes('напит') || c.includes('сусын') || c.includes('beverage')) return '#E98A6A';
    if (c.includes('dessert') || c.includes('десерт')) return '#EBB86A';
    if (c.includes('salad') || c.includes('салат')) return '#C8986A';
    if (c.includes('soup') || c.includes('суп') || c.includes('сорпа')) return '#F0D89A';
    if (c.includes('pizza') || c.includes('пицца')) return '#E8A87C';
    if (c.includes('burger') || c.includes('бургер')) return '#D4A056';
    return '#F4C77A';
  };

  const renderCard = (u: UpsellItem) => {
    const items = getCartItemsForProduct(u.id);
    const totalQty = getTotalQtyForProduct(u.id);
    const st = u.product.stockToday;
    const maxStock = st != null && !isNaN(Number(st)) && Number(st) >= 0 ? Number(st) : null;
    const atMax = maxStock !== null && totalQty >= maxStock;
    const editable = hasOptions(u);
    const handleCardClick = () => {
      track('upsell_clicked', {
        orderId: order?.id,
        upsellId: u.id,
        productId: u.product.id,
        productName: u.product.name,
      });
      // Persist click to populate UpsellRecommendation.clickedAt funnel signal
      if (order?.id) {
        axios.post(`${API_URL}/api/orders/${order.id}/upsell-click`, { productId: u.product.id }).catch(() => {});
      }
      if (items.length === 0 && maxStock !== 0) addToCart(u.id, u, editable);
    };
    const tint = tintFor(u.product.category);
    const tintAlt = tint + 'CC';
    const categoryLabel = u.product.category || u.product.restaurantName || t('orderStatus.defaultCategory', 'К заказу');
    const mediaStyle: React.CSSProperties = u.product.imageUrl
      ? { backgroundImage: `url(${u.product.imageUrl})` }
      : ({ ['--os-card-tint' as any]: tint, ['--os-card-tint-2' as any]: tintAlt } as React.CSSProperties);
    return (
      <motion.div key={u.id} className="os-upsell-card" onClick={handleCardClick} whileHover={{ y: -2 }}>
        <div
          className={`os-upsell-media${u.product.imageUrl ? ' os-upsell-media--img' : ''}`}
          style={mediaStyle}
        >
          <span className="os-upsell-cat-pill">{categoryLabel}</span>
          {((u.product as any).popularityToday ?? 0) >= 2 && (
            <button
              type="button"
              className={`os-upsell-pop-badge${popOpenId === u.id ? ' is-open' : ''}`}
              onClick={(e) => { e.stopPropagation(); setPopOpenId(popOpenId === u.id ? null : u.id); }}
              aria-label={t('orderStatus.popularityTitle', 'Заказывали сегодня')}
            >
              <span className="os-upsell-pop-num">{(u.product as any).popularityToday}</span>
              <span className="os-upsell-pop-text">{t('orderStatus.popularityFullSuffix', 'раз заказывали сегодня')}</span>
              {popOpenId === u.id && (
                <span className="os-upsell-pop-tip" onClick={(e) => e.stopPropagation()}>
                  <strong>{t('orderStatus.popularityPopupTitle', 'Хит сегодня')}</strong>
                  {t('orderStatus.popularityPopupBody', { count: (u.product as any).popularityToday, defaultValue: 'Эту позицию докупили {{count}} раз за последние 24 часа — гости часто берут вместе с основным заказом.' })}
                </span>
              )}
            </button>
          )}
        </div>
        <div className="os-upsell-body">
          <div className="os-upsell-name">{u.product.name}</div>
          {u.product.restaurantName && u.product.restaurantName !== order?.restaurant?.name && (
            <div className="os-upsell-from">{t('orderStatus.fromRestaurant', 'из')} {u.product.restaurantName}</div>
          )}
          {u.product.description && (
            <div className="os-upsell-desc">{u.product.description}</div>
          )}
          {(u as any).reason && (
            <div className="os-upsell-reason">{(u as any).reason}</div>
          )}
          {items.length > 0 && editable ? (
            <div className="os-upsell-variants">
              {items.map((item) => {
                const itemAtMax = maxStock !== null && totalQty >= maxStock;
                return (
                  <div key={item.tempId} className="os-upsell-variant-row">
                    <div className="os-upsell-variant-info">
                      <span className="os-upsell-variant-custom">
                        {formatCustomization(item.customization) || t('orderStatus.default')}
                      </span>
                      <span className="os-upsell-variant-price">₸{getUnitPrice(u, item.customization).toLocaleString('ru')}</span>
                    </div>
                    <div className="os-upsell-variant-controls">
                      <div className="os-upsell-qty">
                        <button
                          type="button"
                          className="os-upsell-qty-btn"
                          onClick={(e) => { e.stopPropagation(); changeQty(item.tempId, -1); }}
                          aria-label={t('orderStatus.removeOne')}
                        ><Minus size={12} strokeWidth={2.5} /></button>
                        <span className="os-upsell-count">{item.quantity}</span>
                        <button
                          type="button"
                          className="os-upsell-qty-btn is-add"
                          onClick={(e) => { e.stopPropagation(); if (!itemAtMax) changeQty(item.tempId, 1); }}
                          aria-label={t('orderStatus.addOne')}
                          disabled={itemAtMax}
                        ><Plus size={12} strokeWidth={2.5} /></button>
                      </div>
                      <button
                        type="button"
                        className="os-upsell-edit-btn"
                        onClick={(e) => { e.stopPropagation(); setCustomizeUpsell({ product: u, cartItem: item }); }}
                      >{t('orderStatus.edit')}</button>
                      <button
                        type="button"
                        className="os-upsell-remove-variant-btn"
                        onClick={(e) => { e.stopPropagation(); removeFromCart(item.tempId); }}
                        title={t('orderStatus.remove')}
                      ><X size={12} strokeWidth={2.5} /></button>
                    </div>
                  </div>
                );
              })}
              <button
                type="button"
                className="os-upsell-add-another-btn"
                onClick={(e) => { e.stopPropagation(); addToCart(u.id, u, true); }}
                disabled={atMax}
              >+ {t('orderStatus.addAnotherVariant')}</button>
            </div>
          ) : (
            <div className="os-upsell-footer">
              <span className="os-upsell-price">₸{getUnitPrice(u).toLocaleString('ru')}</span>
              {items.length > 0 ? (
                <div className="os-upsell-qty">
                  <button
                    type="button"
                    className="os-upsell-qty-btn"
                    onClick={(e) => { e.stopPropagation(); changeQty(items[0].tempId, -1); }}
                    aria-label={t('orderStatus.removeOne')}
                  ><Minus size={12} strokeWidth={2.5} /></button>
                  <span className="os-upsell-count">{totalQty}</span>
                  <button
                    type="button"
                    className="os-upsell-qty-btn is-add"
                    onClick={(e) => { e.stopPropagation(); if (!atMax) changeQty(items[0].tempId, 1); }}
                    aria-label={t('orderStatus.addOne')}
                    disabled={atMax}
                  ><Plus size={12} strokeWidth={2.5} /></button>
                </div>
              ) : (
                <button
                  type="button"
                  className="os-upsell-add"
                  onClick={(e) => { e.stopPropagation(); addToCart(u.id, u, editable); }}
                  disabled={maxStock === 0}
                  aria-label={t('orderStatus.addToOrder')}
                ><Plus size={14} strokeWidth={2.5} /></button>
              )}
            </div>
          )}
          {items.length === 0 && maxStock !== null && maxStock > 0 && maxStock <= 5 && (
            <div className="os-upsell-stock-hint">({maxStock} left)</div>
          )}
          {items.length > 0 && maxStock !== null && (
            <div className="os-upsell-stock-row">({Math.max(0, maxStock - totalQty)} left)</div>
          )}
        </div>
      </motion.div>
    );
  };

  const cartCount = cartItems.reduce((sum, c) => sum + c.quantity, 0);
  const stickyVisible = (cartCount > 0 || paymentProcessing) && activeTab === 'order' && !['COMPLETED', 'CANCELLED'].includes(order.status);
  const stickyItemsLine = Array.from(new Set(
    cartItems.map((c) => visible.find((v) => v.id === c.productId)?.product.name).filter(Boolean) as string[]
  )).join(' · ');

  return (
    <div className="order-status-container">
      <motion.div
        className="os-root"
        animate={order.status === 'READY' ? {
          boxShadow: ['0 0 8px rgba(212,132,58,0.15)', '0 0 24px rgba(212,132,58,0.4)'],
        } : { boxShadow: '0 0 0px rgba(212,132,58,0)' }}
        transition={{ duration: 1.5, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
      >
        {isOffline && (
          <div className="os-offline-banner">
            <Wifi size={16} />
            <div>
              <div style={{ fontWeight: 700 }}>{t('orderStatus.offlineTitle', 'Нет соединения')}</div>
              <div style={{ opacity: 0.75 }}>{t('order.offlineBanner', 'Показываем последнее известное состояние')}</div>
            </div>
          </div>
        )}

        <header className="os-header">
          {isAuthenticated ? (
            <button
              className="os-back-btn"
              onClick={() => navigate('/')}
              aria-label="Back"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
            </button>
          ) : (
            <div style={{ width: 36 }} />
          )}
          <div className="os-header-brand">
            {order.restaurant?.logoUrl && (
              <img
                src={order.restaurant.logoUrl}
                alt={order.restaurant.name}
                className="os-header-logo"
              />
            )}
            <div className="os-header-text">
              <div className="os-header-title">{order.restaurant?.name || 'OLSO'}</div>
              {order.restaurant?.venue?.name && (
                <div className="os-header-sub">{order.restaurant.venue.name}</div>
              )}
            </div>
          </div>
          <div className="os-header-actions">
            <CustomerUserMenu />
          </div>
        </header>

        <div className="os-tabs">
          <button
            className={`os-tab ${activeTab === 'order' ? 'os-tab--active' : ''}`}
            onClick={() => setActiveTab('order')}
          >
            {t('menu.tabOrder')}
          </button>
          <button
            ref={menuTabRef}
            className={`os-tab ${activeTab === 'menu' ? 'os-tab--active' : ''}`}
            onClick={() => setActiveTab('menu')}
          >
            {t('menu.tabMenu')}
            {menuProducts.length > 0 && (
              <span className="os-tab-badge">{menuProducts.length}</span>
            )}
          </button>
        </div>

        {activeTab === 'menu' ? (
        <div className="os-menu-view">
          <div className="os-menu-search-wrap">
            <span className="os-menu-search-icon"><Search size={15} strokeWidth={2.5} /></span>
            <input
              className="os-menu-search-input"
              type="search"
              placeholder={t('menu.searchPlaceholder')}
              value={menuSearchQuery}
              onChange={(e) => setMenuSearchQuery(e.target.value)}
            />
            {menuSearchQuery && (
              <button className="os-menu-search-clear" onClick={() => setMenuSearchQuery('')} aria-label={t('common.close')}><X size={14} /></button>
            )}
          </div>

          {menuCategories.length > 1 && (
            <div className="os-menu-categories">
              <button
                className={`os-menu-cat-chip ${menuActiveCategory === null ? 'os-menu-cat-chip--active' : ''}`}
                onClick={() => setMenuActiveCategory(null)}
              >{t('menu.all')}</button>
              {menuCategories.map((cat) => (
                <button
                  key={cat}
                  className={`os-menu-cat-chip ${menuActiveCategory === cat ? 'os-menu-cat-chip--active' : ''}`}
                  onClick={() => setMenuActiveCategory(cat)}
                >{cat}</button>
              ))}
            </div>
          )}

          {menuLoading ? (
            <div className="os-menu-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="os-upsell-card skeleton-card">
                  <div className="os-upsell-media">
                    <div className="skeleton-block" style={{ width: '100%', height: '100%' }} />
                  </div>
                  <div className="os-upsell-body" style={{ gap: '8px' }}>
                    <div className="skeleton-block" style={{ width: '90%', height: '14px', borderRadius: '4px' }} />
                    <div className="skeleton-block" style={{ width: '60%', height: '14px', borderRadius: '4px' }} />
                    <div className="skeleton-block" style={{ width: '40%', height: '16px', borderRadius: '4px', marginTop: '4px' }} />
                    <div className="skeleton-block" style={{ width: '100%', height: '36px', borderRadius: '10px', marginTop: '4px' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : menuError ? (
            <div className="os-menu-error">
              <div className="os-menu-error-icon"><Frown size={36} strokeWidth={1.5} /></div>
              <div className="os-menu-error-text">{menuError}</div>
              <button className="retry-btn" onClick={fetchMenu}>{t('common.retry')}</button>
            </div>
          ) : Object.keys(menuByCategory).length === 0 ? (
            <div className="os-menu-empty">
              <div className="os-menu-empty-icon"><Search size={28} strokeWidth={1.5} /></div>
              <div>{t('menu.notFound')}</div>
            </div>
          ) : (
            Object.entries(menuByCategory).map(([category, products]) => (
              <div key={category} className="os-menu-category-group">
                <div className="os-menu-category-title">{category}</div>
                <div className="os-menu-grid">
                  {products.map((product) => renderCard({
                    id: product.id,
                    product: {
                      id: product.id,
                      name: product.name,
                      description: product.description,
                      price: product.price,
                      imageUrl: product.imageUrl,
                      category: product.category ?? undefined,
                      stockToday: product.stockToday,
                      isEditable: product.isEditable,
                      optionGroups: product.optionGroups,
                    },
                  }))}
                </div>
              </div>
            ))
          )}
        </div>
        ) : (
        <>
        {(() => {
          const isReady = order.status === 'READY';
          const isCompleted = order.status === 'COMPLETED';
          const isGreen = isReady || isCompleted;
          const statusOrder = ['PENDING', 'PREPARING', 'READY', 'COMPLETED'];
          const currentIdx = statusOrder.indexOf(order.status);
          const segments = [
            { activeAt: 0, label: t('orderStatusSteps.ordered') },
            { activeAt: 1, label: t('orderStatusSteps.cooking') },
            { activeAt: 2, label: t('orderStatusSteps.ready') },
            { activeAt: 3, label: t('orderStatus.segmentPickedUp', 'Забран') },
          ];
          const heroIcon = isGreen
            ? <CheckCircle size={32} strokeWidth={2} />
            : order.status === 'PREPARING'
              ? <ChefHat size={32} strokeWidth={2} />
              : <ClipboardList size={32} strokeWidth={2} />;
          const dotColor = isGreen ? 'var(--os-green-deep)' : 'var(--a-amber)';
          const heroSub = isReady
            ? t('orderStatus.readySub', 'Подходите к стойке за заказом')
            : isCompleted
              ? t('orderStatus.thanksTitle', 'Спасибо за заказ!')
              : order.status === 'PREPARING'
                ? t('orderStatus.preparingHint', 'Готовим ваш заказ')
                : t('orderStatus.pendingHint', 'Заказ принят, ожидает приготовления');
          return (
            <>
            <AnimatePresence>
              {heroOutOfView && (
                <motion.div
                  key="os-sticky-mini"
                  className={`os-sticky-mini ${isGreen ? 'os-sticky-mini--green' : ''}`}
                  initial={{ y: '-100%', opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: '-100%', opacity: 0 }}
                  transition={{ duration: 0.14, ease: [0.4, 0, 0.2, 1] }}
                >
                  <div className="os-sticky-mini-inner">
                    <div className="os-sticky-mini-left">
                      <span className="os-sticky-mini-restaurant">{order.restaurant?.name || ''}</span>
                      <span className="os-sticky-mini-sep">·</span>
                      <span className="os-sticky-mini-orderNo">#{order.id.slice(-6).toUpperCase()}</span>
                    </div>
                    <div className="os-sticky-mini-pill" style={{ color: dotColor }}>
                      <span className="os-sticky-mini-dot" style={{ background: dotColor }} />
                      <span className="os-sticky-mini-status">{status}</span>
                    </div>
                  </div>
                  <div className="os-sticky-mini-progress">
                    {[0, 1, 2, 3].map((i) => {
                      const done = i <= currentIdx;
                      const segClass = done
                        ? (isGreen ? 'os-sticky-mini-seg--done-green' : 'os-sticky-mini-seg--done')
                        : '';
                      return (
                        <div
                          key={i}
                          className={`os-sticky-mini-seg ${segClass}`}
                        />
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <section
              ref={statusBarRef}
              className={`os-hero ${isGreen ? 'os-hero--green' : ''} ${statusChanged ? 'os-hero--changed' : ''}`}
            >
              <div className="os-hero-topRow">
                <div>
                  <div className="os-hero-orderLabel">{t('orderStatus.orderLabel', 'Заказ')}</div>
                  <div className="os-hero-orderNo">#{order.id.slice(-6).toUpperCase()}</div>
                </div>
                <div className="os-hero-pill" style={{ color: dotColor }}>
                  <motion.div
                    className="os-hero-dot"
                    style={{ background: dotColor }}
                    animate={{ boxShadow: [`0 0 0 0 ${isGreen ? 'rgba(74,124,89,0.45)' : 'rgba(255,90,0,0.45)'}`, `0 0 0 8px ${isGreen ? 'rgba(74,124,89,0)' : 'rgba(255,90,0,0)'}`] }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeOut' }}
                  />
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={order.status}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.2 }}
                    >{status}</motion.span>
                  </AnimatePresence>
                </div>
              </div>

              <div className="os-hero-body">
                <div className="os-hero-icon" style={{ color: dotColor }}>
                  {heroIcon}
                </div>
                <div className="os-hero-textBlock">
                  <div className="os-hero-title">{status}</div>
                  <div className="os-hero-sub">{heroSub}</div>
                </div>
              </div>

              <div className="os-progress">
                {[0, 1, 2, 3].map((i) => {
                  const done = i <= currentIdx;
                  // Color ONLY completed segments. Green if status passed READY.
                  const segClass = done
                    ? (isGreen ? 'os-progress-seg--done-green' : 'os-progress-seg--done')
                    : '';
                  return (
                    <div
                      key={i}
                      className={`os-progress-seg ${segClass}`}
                    />
                  );
                })}
              </div>
              <div className="os-progress-labels">
                {segments.map((seg, i) => (
                  <span key={i} className={i <= currentIdx ? 'is-active' : ''}>{seg.label}</span>
                ))}
              </div>

              {isReady && (
                <div className="os-cta-ready">
                  {t('orderStatus.pickupCta', 'Подходите к стойке')}
                </div>
              )}
              {isCompleted && (
                (order.reviewed || ratingDone) ? (
                  <div className="os-cta-rate os-cta-rate--done">
                    {t('orderStatus.rateThanks', 'Спасибо за оценку')} ⭐
                  </div>
                ) : (
                  <button type="button" className="os-cta-rate" onClick={() => setRatingOpen(true)}>
                    {t('orderStatus.rateOrder', 'Оценить заказ')}
                  </button>
                )
              )}
            </section>
            </>
          );
        })()}

        {/* Venue sub-orders — shown below main status */}
        {order.status === 'COMPLETED' ? (
          <AnimatePresence>
            {venueOrders.filter(vo => !['COMPLETED', 'CANCELLED'].includes(vo.status)).map(vo => {
              const venueSteps = [
                { key: 'PENDING', label: t('orderStatusSteps.ordered') },
                { key: 'PREPARING', label: t('orderStatusSteps.cooking') },
                { key: 'READY', label: t('orderStatusSteps.ready') },
              ];
              const currentStep = venueSteps.findIndex(s => s.key === vo.status);
              return (
                <motion.div
                  key={vo.id}
                  className="os-venue-expanded"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                >
                  <div className="os-venue-card-header">
                    <span className="os-venue-card-icon"><ChefHat size={16} /></span>
                    <span className="os-venue-card-name">{vo.restaurantName}</span>
                  </div>
                  <div className="os-venue-progress" style={{ marginTop: 10 }}>
                    {venueSteps.map((step, i) => (
                      <span key={step.key} style={{ display: 'contents' }}>
                        <div className="os-venue-step">
                          <div className={`os-venue-step-dot ${i <= currentStep ? 'is-active' : ''} ${i === currentStep ? 'is-current' : ''}`}>
                            {i <= currentStep ? <Check size={10} strokeWidth={3} /> : ''}
                          </div>
                          <span className={`os-venue-step-label ${i <= currentStep ? 'is-active' : ''}`}>{step.label}</span>
                        </div>
                        {i < venueSteps.length - 1 && (
                          <div className={`os-venue-progress-line ${i < currentStep ? 'is-active' : ''}`} />
                        )}
                      </span>
                    ))}
                  </div>
                  {vo.items && vo.items.length > 0 && (
                    <div className="os-venue-items-list">
                      {vo.items.map((item, idx) => (
                        <div key={idx} className="os-venue-item-row">
                          <span>{item.name} × {item.quantity}</span>
                          <span>{(item.price * item.quantity).toLocaleString()} ₸</span>
                        </div>
                      ))}
                      {vo.totalAmount != null && (
                        <div className="os-venue-item-total">
                          <span>{t('orderStatus.total', { defaultValue: 'Итого' })}</span>
                          <span>{vo.totalAmount.toLocaleString()} ₸</span>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        ) : (
          <AnimatePresence>
            {venueOrders.filter(vo => !['COMPLETED', 'CANCELLED'].includes(vo.status)).map(vo => {
              const venueSteps = [
                { key: 'PENDING', label: t('orderStatusSteps.ordered') },
                { key: 'PREPARING', label: t('orderStatusSteps.cooking') },
                { key: 'READY', label: t('orderStatusSteps.ready') },
              ];
              const currentStep = venueSteps.findIndex(s => s.key === vo.status);
              const voReady = vo.status === 'READY';
              return (
                <motion.div
                  key={vo.id}
                  className={`os-venue-card ${voReady ? 'os-venue-card--ready' : ''}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                  onClick={() => openVenueDetail(vo)}
                >
                  <div className="os-venue-card-header">
                    <span className="os-venue-card-icon">{voReady ? <CheckCircle2 size={16} /> : <ChefHat size={16} />}</span>
                    <span className="os-venue-card-name">{vo.restaurantName}</span>
                  </div>
                  <div className="os-venue-card-text">
                    {voReady
                      ? t('orderStatus.venueReady', { restaurant: vo.restaurantName })
                      : t('orderStatus.venuePreparing', { restaurant: vo.restaurantName })}
                  </div>
                  <div className="os-venue-progress">
                    {venueSteps.map((step, i) => (
                      <span key={step.key} style={{ display: 'contents' }}>
                        <div className="os-venue-step">
                          <div className={`os-venue-step-dot ${i <= currentStep ? 'is-active' : ''} ${i === currentStep ? 'is-current' : ''}`}>
                            {i <= currentStep ? <Check size={10} strokeWidth={3} /> : ''}
                          </div>
                          <span className={`os-venue-step-label ${i <= currentStep ? 'is-active' : ''}`}>
                            {step.label}
                          </span>
                        </div>
                        {i < venueSteps.length - 1 && (
                          <div className={`os-venue-progress-line ${i < currentStep ? 'is-active' : ''}`} />
                        )}
                      </span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}

        {isAuthenticated ? (
          <button
            ref={passportRef}
            type="button"
            className="os-passport-strip"
            onClick={handlePassportClick}
          >
            <span className="os-passport-tile">
              <Star size={20} strokeWidth={2} />
              <span className="os-passport-badge">+5</span>
            </span>
            <span className="os-passport-text">
              <span className="os-passport-title">{t('foodPassport.title')}</span>
              <span className="os-passport-sub">{t('foodPassport.subcaption')}</span>
            </span>
            <span className="os-passport-cta">
              <ArrowRight size={14} strokeWidth={2.5} />
            </span>
          </button>
        ) : (
          <button
            ref={passportRef}
            type="button"
            className="os-auth-prompt"
            onClick={() => setShowAuthModal(true)}
          >
            <span className="os-auth-prompt-glow" />
            <span className="os-auth-prompt-icon">
              <Sparkles size={20} strokeWidth={2} />
            </span>
            <span className="os-auth-prompt-text">
              <span className="os-auth-prompt-title">{t('orderStatus.authPromptTitle', 'Копите штампы и подарки')}</span>
              <span className="os-auth-prompt-sub">{t('orderStatus.authPromptSub', 'Войдите — начислим бонус за заказ')}</span>
            </span>
            <span className="os-auth-prompt-btn">{t('orderStatus.signIn', 'Войти')}</span>
          </button>
        )}

        {(() => {
          const itemsCount = order.items.reduce((s, it) => s + it.quantity, 0);
          // item.price is the price charged at order time — it already carries
          // the customization modifiers (size, extras) and is immune to later
          // menu price changes. product.price is the current base price and
          // would disagree with both the order total and the POS check.
          const itemsTotal = order.items.reduce((s, it) => s + it.price * it.quantity, 0);
          return (
            <section className="os-items">
              <button
                type="button"
                className="os-items-summary"
                onClick={() => setItemsExpanded(v => !v)}
                aria-expanded={itemsExpanded}
              >
                <div className="os-items-summary-left">
                  <span className="os-items-label">{t('orderStatus.yourOrderLabel', 'Ваш заказ')}</span>
                  <span className="os-items-count-pill">{itemsCount}</span>
                </div>
                <div className="os-items-summary-right">
                  <span className="os-items-total">{itemsTotal.toLocaleString('ru')} ₸</span>
                  <span className="os-items-chevron">
                    {itemsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </span>
                </div>
              </button>
              <AnimatePresence initial={false}>
                {itemsExpanded && (
                  <motion.div
                    className="os-items-expanded"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.22, ease: 'easeInOut' }}
                  >
                    <div className="os-items-expanded-inner">
                      {order.items.map((item) => {
                        const lineTotal = item.price * item.quantity;
                        return (
                          <div key={item.id} className="os-item-row">
                            <div className="os-item-tile">
                              <UtensilsCrossed size={16} strokeWidth={2} />
                            </div>
                            <div className="os-item-body">
                              <div className="os-item-name">{item.product.name}</div>
                              {(item as any).customization && Object.keys((item as any).customization).length > 0 && (
                                <div className="os-item-custom">{formatCustomization((item as any).customization)}</div>
                              )}
                              {item.quantity > 1 && (
                                <div className="os-item-qty">×{item.quantity}</div>
                              )}
                              {(item as any).giftedBy && (
                                <div
                                  className="os-item-gift"
                                  onClick={(e) => { e.stopPropagation(); setGiftModal({ senderName: (item as any).giftedBy.name || 'Друг', productName: item.product?.name || '' }); }}
                                >
                                  <Gift size={11} strokeWidth={2.5} />
                                  {t('orderStatus.giftFrom', { name: (item as any).giftedBy.name || 'друга' })}
                                </div>
                              )}
                            </div>
                            <div className="os-item-price">{lineTotal.toLocaleString('ru')} ₸</div>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </section>
          );
        })()}

        {upsellsLoading ? (
          <section className="os-upsell">
            <div className="os-upsell-head">
              <div>
                <div className="os-upsell-title">{t('orderStatus.addToOrderTitle', 'Добавить к заказу')}</div>
                <div className="os-upsell-sub">{t('orderStatus.addToOrderSub', 'Кухня приготовит вместе с основным')}</div>
              </div>
              <span className="os-upsell-chip">{t('orderStatus.upsellCutoffChip', 'До готовности')}</span>
            </div>
            <div className="os-upsell-grid">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="os-upsell-card skeleton-card">
                  <div className="os-upsell-media">
                    <div className="skeleton-block" style={{ width: '100%', height: '100%' }} />
                  </div>
                  <div className="os-upsell-body" style={{ gap: '8px' }}>
                    <SkeletonBlock width="90%" height="14px" radius="4px" />
                    <SkeletonBlock width="60%" height="14px" radius="4px" />
                    <SkeletonBlock width="40%" height="16px" radius="4px" style={{ marginTop: 4 }} />
                    <SkeletonBlock width="100%" height="36px" radius="10px" style={{ marginTop: 4 }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : visible.length > 0 ? (
          (() => {
            const isReady = order?.status === 'READY';
            const filterStock = (u: UpsellItem) => {
              const st = u.product.stockToday;
              if (!(st == null || !isNaN(Number(st)) && Number(st) > 0)) return false;
              if (isReady && !u.product.instantGiveout) return false;
              return true;
            };
            const tagBasedR1 = order?.restaurant?.preferences?.tagBasedR1 !== false;
            const { ownCards: rawOwn, venueCards: rawVenue } = sliceUpsells(
              order?.recommendations ?? null,
              tagBasedR1
            );
            const ownCards = rawOwn.filter(filterStock);
            const venueCards = isReady ? [] : rawVenue.filter(filterStock);

            const headingTitle = isReady
              ? t('orderStatus.grabOnYourWay', 'Захватите с собой')
              : t('orderStatus.addToOrderTitle', 'Добавить к заказу');
            const headingSub = isReady
              ? t('orderStatus.grabOnYourWaySub', 'Быстрая выдача')
              : t('orderStatus.addToOrderSub', 'Кухня приготовит вместе с основным');

            return (
              <>
              {(order?.offers?.length ?? 0) > 0 && order?.status !== 'READY' && (
                <div className="os-upgrade-strip">
                  <div className="os-upgrade-strip-title">{t('orderStatus.upgradeTitle', 'Улучши заказ')}</div>
                  {order!.offers!
                    .filter((o) => !appliedOfferIds.includes(o.id))
                    .slice(0, 2)
                    .map((offer) => {
                      const isAddon = offer.type === 'DISCOUNTED_ADDON';
                      const headline = offer.title
                        || (isAddon
                          ? offer.addItems.map((a) => a.name).join(' + ')
                          : `${offer.triggerProductName} → ${offer.addItems.map((a) => a.name).join(' + ')}`);
                      const img = offer.addItems.find((a) => a.imageUrl)?.imageUrl || null;
                      const saving = Math.max(0, Math.round(offer.compareAtPrice - offer.bundlePrice));
                      return (
                        <div key={offer.id} className={`os-upgrade-card os-upgrade-card--${isAddon ? 'addon' : 'upgrade'}`}>
                          {img && <div className="os-upgrade-media"><img src={img} alt="" /></div>}
                          <div className="os-upgrade-body">
                            <div className="os-upgrade-headline">{headline}</div>
                            {!isAddon && (
                              <div className="os-upgrade-sub">+ {offer.addItems.map((a) => a.name).join(', ')}</div>
                            )}
                            <div className="os-upgrade-pricerow">
                              {isAddon ? (
                                <>
                                  <span className="os-upgrade-compare">₸{Math.round(offer.compareAtPrice)}</span>
                                  <span className="os-upgrade-price">₸{Math.round(offer.bundlePrice)}</span>
                                  {saving > 0 && <span className="os-upgrade-save">−₸{saving}</span>}
                                </>
                              ) : (
                                <span className="os-upgrade-delta">+₸{Math.round(offer.bundlePrice)}</span>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="os-upgrade-cta"
                            disabled={applyingOfferId === offer.id}
                            onClick={() => applyOffer(offer)}
                          >
                            {applyingOfferId === offer.id
                              ? t('orderStatus.adding', 'Добавляем…')
                              : isAddon
                                ? t('orderStatus.offerAdd', 'Добавить')
                                : t('orderStatus.offerMakeCombo', 'Сделать комбо')}
                          </button>
                        </div>
                      );
                    })}
                </div>
              )}
              <section className="os-upsell">
                <div className="os-upsell-head">
                  <div>
                    <div className="os-upsell-title">{headingTitle}</div>
                    <div className="os-upsell-sub">{headingSub}</div>
                  </div>
                  {!isReady && (
                    <span className="os-upsell-chip">{t('orderStatus.upsellCutoffChip', 'До готовности')}</span>
                  )}
                </div>

                {/* Repeat memory */}
                {repeatItems.length > 0 && !repeatDismissed && (
                  <div className="os-repeat-memory">
                    <div className="os-repeat-memory-head">
                      <span className="os-repeat-memory-title">{t('orderStatus.repeatMemoryTitle', 'В прошлый раз вы брали')}</span>
                      <button
                        type="button"
                        className="os-repeat-memory-close"
                        onClick={() => {
                          setRepeatDismissed(true);
                          sessionStorage.setItem(`repeat-dismissed-${order?.id}`, '1');
                        }}
                        aria-label={t('common.close')}
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <div className="os-repeat-memory-items">
                      {repeatItems.map((item) => (
                        <div key={item.productId} className="os-repeat-memory-chip">
                          {item.imageUrl && (
                            <img src={item.imageUrl} alt="" className="os-repeat-memory-chip-img" />
                          )}
                          <span className="os-repeat-memory-chip-name">{item.name}</span>
                          <span className="os-repeat-memory-chip-price">{item.price} ₸</span>
                          <button
                            type="button"
                            className="os-repeat-memory-chip-add"
                            onClick={() => addToCart(item.productId)}
                            aria-label={t('orderStatus.addToOrder')}
                          >
                            <Plus size={12} strokeWidth={2.5} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Social proof bubble — disabled in favor of per-card popularity badges */}
                {false && socialProofCount > 0 && !socialProofDismissed && (
                  <div className="os-social-bubble">
                    <span className="os-social-bubble-icon"><Sparkles size={14} strokeWidth={2.5} /></span>
                    <span className="os-social-bubble-text">
                      {t('orderStatus.socialProofText', { count: socialProofCount, defaultValue: 'Сегодня {{count}} человек добавили к заказу' })}
                    </span>
                    <button
                      type="button"
                      className="os-social-bubble-close"
                      onClick={() => setSocialProofDismissed(true)}
                      aria-label={t('common.close')}
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}

                {/* Own restaurant upsells */}
                {ownCards.length > 0 && (
                  <div ref={ownUpsellsRef} className="os-upsell-grid">
                    {ownCards.map((u) => renderCard(u))}
                  </div>
                )}

                {/* Venue divider + venue cards */}
                {venueCards.length > 0 && (
                  <>
                    <div className="os-venue-divider">
                      {t('orderStatus.venueDividerTemplate', { venue: order?.restaurant?.venue?.name || t('orderStatus.foodcourt', 'фудкорте'), defaultValue: 'Также в {{venue}}' })}
                    </div>
                    <div ref={venueUpsellsRef} className="os-upsell-grid">
                      {venueCards.map((u) => renderCard(u))}
                    </div>
                  </>
                )}
              </section>
              </>
            );
          })()
        ) : ['COMPLETED', 'CANCELLED'].includes(order.status) ? (
          <div className={`os-completed os-completed--${order.status.toLowerCase()}`}>
            <div className="os-completed-icon">
              {order.status === 'COMPLETED' ? <CheckCircle2 size={40} strokeWidth={1.8} /> : <Frown size={40} strokeWidth={1.6} />}
            </div>
            <div className="os-completed-title">
              {order.status === 'COMPLETED' ? t('orderStatus.thanksTitle') : t('orderStatus.cancelledTitle')}
            </div>
            {order.status === 'COMPLETED' && (
              <>
                {isAuthenticated ? (
                  <div className="os-completed-points">
                    +{Math.floor(order.totalAmount / 10)} {t('orderStatus.pointsEarned')}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="os-completed-points os-completed-points--guest"
                    onClick={() => setShowAuthModal(true)}
                  >
                    {t('orderStatus.pointsGuest', '+{{points}} баллов за заказ — войдите, чтобы забрать 🎁', { points: Math.floor(order.totalAmount / 10) })}
                  </button>
                )}
                {(order.reviewed || ratingDone) ? (
                  <button type="button" className="os-completed-finish-btn" onClick={() => navigate('/')}>
                    {t('orderStatus.finishOrder', 'На главную')}
                  </button>
                ) : (
                  <button type="button" className="os-completed-finish-btn" onClick={() => setRatingOpen(true)}>
                    {t('orderStatus.rateOrder', 'Оценить заказ')}
                  </button>
                )}
              </>
            )}
          </div>
        ) : order.status === 'READY' ? (
          <div className="os-no-upsells">
            <div className="os-no-upsells-icon"><CheckCircle2 size={28} strokeWidth={1.8} /></div>
            <div className="os-no-upsells-text">
              {isAuthenticated
                ? t('orderStatus.readyEmptyMsg', 'Заказ готов — заберите у стойки. +{{points}} баллов за заказ.', { points: Math.floor(order.totalAmount / 10) })
                : t('orderStatus.readyGuest', 'Заказ готов — заберите у стойки. Войдите и заберите {{points}} баллов 🎁', { points: Math.floor(order.totalAmount / 10) })}
            </div>
          </div>
        ) : (
          <div className="os-no-upsells">
            <div className="os-no-upsells-icon"><Bell size={24} strokeWidth={1.8} /></div>
            <div className="os-no-upsells-text">{t('orderStatus.checkBackSoon')}</div>
          </div>
        )}

        <div className="os-footer-note">{t('orderStatus.footerNote')}</div>
        </>
        )}

        {debugMode && order && (
          <DebugPanel
            order={order}
            debugInfo={debugInfo}
            apiUrl={API_URL || ''}
            onRefresh={handleRefresh}
          />
        )}

        {customizeUpsell && hasOptions(customizeUpsell.product)
          ? createPortal(
              <CustomizationModal
                productName={customizeUpsell.product.product.name}
                basePrice={customizeUpsell.product.product.price}
                optionGroups={customizeUpsell.product.product.optionGroups!}
                initialCustomization={customizeUpsell.cartItem?.customization || null}
                onSave={(cust) => {
                  if (customizeUpsell.cartItem) {
                    updateCartItemCustomization(customizeUpsell.cartItem.tempId, cust);
                  }
                  setCustomizeUpsell(null);
                }}
                onClose={() => setCustomizeUpsell(null)}
              />,
              document.body
            )
          : null}

        {/* Venue order detail popup */}
        {venueDetailId && createPortal(
          <div className="os-venue-detail-overlay" onClick={() => setVenueDetailId(null)}>
            <motion.div
              className="os-venue-detail-modal"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            >
              <button type="button" className="os-venue-detail-close" onClick={() => setVenueDetailId(null)} aria-label={t('common.close')}><X size={18} /></button>
              {venueDetailLoading ? (
                <div className="os-venue-detail-loading">{t('common.loading')}</div>
              ) : venueDetailData ? (
                <>
                  <div className="os-venue-detail-header">
                    <span className="os-venue-detail-icon"><UtensilsCrossed size={20} /></span>
                    <div>
                      <div className="os-venue-detail-restaurant">{venueDetailData.restaurantName}</div>
                      <div className="os-venue-detail-status">
                        {venueDetailData.status === 'READY'
                          ? t('orderStatus.venueReady', { restaurant: venueDetailData.restaurantName })
                          : t('orderStatus.venuePreparing', { restaurant: venueDetailData.restaurantName })}
                      </div>
                    </div>
                  </div>
                  {venueDetailData.items.length > 0 ? (
                    <div className="os-venue-detail-items">
                      {venueDetailData.items.map((item, i) => (
                        <div key={i} className="os-venue-detail-item">
                          <span className="os-venue-detail-item-name">
                            {item.product?.name || '—'}
                          </span>
                          <span className="os-venue-detail-item-qty">×{item.quantity}</span>
                          <span className="os-venue-detail-item-price">{(item.price * item.quantity).toLocaleString('ru')} ₸</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="os-venue-detail-empty">{t('orderStatus.checkBackSoon')}</div>
                  )}
                </>
              ) : null}
            </motion.div>
          </div>,
          document.body
        )}

      </motion.div>

      {/* Sticky cart — outside os-root, anchored to viewport bottom */}
      <AnimatePresence>
        {stickyVisible && (
          <motion.div
            key="os-sticky-cart"
            className={`os-sticky-cart ${paymentProcessing ? 'os-sticky-cart--paying' : ''}`}
            initial={{ y: 120, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
          >
            {paymentProcessing ? (
              <>
                <div className="os-sticky-cart-paying-head">
                  <div>
                    <div className="os-sticky-cart-paying-title">{t('orderStatus.processing', 'Обрабатываем платёж…')}</div>
                    <div className="os-sticky-cart-paying-sub">{t('payment.processingSub', 'Не закрывайте страницу')}</div>
                  </div>
                </div>
                <div className="os-sticky-cart-paying-row">
                  <div className="os-sticky-cart-spinner" />
                  <div className="os-sticky-cart-paying-text">{t('payment.processing', 'Обрабатываем платёж…')}</div>
                </div>
              </>
            ) : (
              <>
                <div className="os-sticky-cart-inner">
                  <div className="os-sticky-cart-badge">{cartCount}</div>
                  <div className="os-sticky-cart-text">
                    <div className="os-sticky-cart-label">{t('orderStatus.additionalToOrder')}</div>
                    <div className="os-sticky-cart-items">{stickyItemsLine}</div>
                  </div>
                  <button
                    type="button"
                    className="os-sticky-cart-pay"
                    onClick={pay}
                    disabled={total <= 0 || processing}
                  >
                    {processing
                      ? t('orderStatus.processing')
                      : `${t('orderStatus.payTotalCta', 'Оплатить')} ${total.toLocaleString('ru')} ₸`}
                    <ArrowRight size={14} strokeWidth={2.5} />
                  </button>
                </div>
                <p className="os-pay-legal os-pay-legal--below">
                  {t('orderStatus.payLegalPrefix', 'Нажимая «Оплатить», вы принимаете')}{' '}
                  <a href="/legal?tab=offer" target="_blank" rel="noreferrer">{t('orderStatus.payLegalOffer', 'оферту')}</a>
                  {' '}{t('common.and', 'и')}{' '}
                  <a href="/legal?tab=refund" target="_blank" rel="noreferrer">{t('orderStatus.payLegalRefund', 'политику возврата')}</a>
                </p>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* READY flash overlay — full-screen green splash */}
      <AnimatePresence>
        {showReadyFlash && (
          <motion.div
            className="os-ready-flash-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={() => setShowReadyFlash(false)}
          >
            <motion.div
              className="os-ready-flash-check"
              initial={{ scale: 0.4, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 320, damping: 18, delay: 0.05 }}
            >
              <CheckCircle size={80} strokeWidth={1.8} color="white" />
            </motion.div>
            <motion.div
              className="os-ready-flash-title"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              {t('orderStatus.readyTitle')}
            </motion.div>
            <motion.div
              className="os-ready-flash-sub"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35, duration: 0.3 }}
            >
              {t('orderStatus.readySub')}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast notification */}
      <AnimatePresence>
        {toast && (
          <motion.div
            className={`os-toast os-toast--${toast.type}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.22 }}
          >
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {showAuthModal && !isAuthenticated && (() => {
        const modal = (
          <FirebaseAuthModal
            orderId={order?.id}
            onClose={() => {
              setShowAuthModal(false);
              setAuthTriggeredByPassport(false);
            }}
            onSuccess={(_customer, token, newBadges) => {
              setShowAuthModal(false);
              setOnboardingDone(true);
              try { localStorage.setItem('olso-onboarding-done', '1'); } catch { /* */ }
              if (authTriggeredByPassport) {
                setAuthTriggeredByPassport(false);
                navigate('/passport');
              }
              handleRefresh();
              if (newBadges && newBadges.length > 0) {
                setBadgeNotifs(newBadges);
              }
              const pendingRef = sessionStorage.getItem('olso_pending_ref');
              if (pendingRef) {
                sessionStorage.removeItem('olso_pending_ref');
                axios.post(`${API_URL || ''}/api/friends/join`, { referralCode: pendingRef }, {
                  headers: { Authorization: `Bearer ${token}` },
                }).catch(() => {});
              }
            }}
          />
        );
        return typeof document !== 'undefined' && document.body
          ? createPortal(modal, document.body)
          : modal;
      })()}

      {/* In-PWA status banner removed — system push notifications cover this. */}

      {currentNotif && createPortal(
        <RewardPopup
          notification={currentNotif}
          queueLength={allNotifs.length}
          onDismiss={dismissCurrent}
          onViewPassport={() => { dismissCurrent(); navigate('/passport'); }}
        />,
        document.body
      )}

      {order && (
        <RatingSheet
          open={ratingOpen}
          onClose={() => setRatingOpen(false)}
          items={order.items}
          isAuthenticated={isAuthenticated}
          submitting={ratingSubmitting}
          onSubmit={submitRating}
        />
      )}

      {giftModal && (
        <GiftReceivedModal show={!!giftModal} senderName={giftModal.senderName} productName={giftModal.productName} onClose={() => setGiftModal(null)} />
      )}

      {showPreparingModal && createPortal(
        <div className="os-preparing-modal-overlay" onClick={() => setShowPreparingModal(false)}>
          <div className="os-preparing-modal" onClick={(e) => e.stopPropagation()}>
            <div className="os-preparing-modal-icon">
              <ChefHat size={36} strokeWidth={1.8} />
            </div>
            <div className="os-preparing-modal-title">{t('orderStatus.preparingModalTitle', 'Ваш заказ готовится!')}</div>
            <div className="os-preparing-modal-subtitle">
              {t('orderStatus.preparingModalSub', 'Последний шанс добавить что-то к заказу')}
            </div>
            <button
              type="button"
              className="os-preparing-modal-btn"
              onClick={() => setShowPreparingModal(false)}
            >
              {t('common.ok', 'Окей')}
            </button>
          </div>
        </div>,
        document.body
      )}

      {!onboardingDone && !isStandalone && order && !upsellsLoading &&
       order.status !== 'COMPLETED' && order.status !== 'CANCELLED' && (
        <OrderOnboarding
          statusBarRef={statusBarRef}
          ownUpsellsRef={ownUpsellsRef}
          venueUpsellsRef={venueUpsellsRef}
          menuTabRef={menuTabRef}
          passportRef={passportRef}
          hasVenueCards={computedHasVenue}
          hasOwnCards={computedHasOwn}
          orderStatus={order.status}
          onSwitchToMenu={() => setActiveTab('menu')}
          onSwitchToOrder={() => setActiveTab('order')}
          onComplete={() => setOnboardingDone(true)}
        />
      )}

      {isStandalone && order && qrCode && (
        <NotificationPrompt qrCode={qrCode} orderId={order.id} />
      )}

      <RefundSheet
        open={showRefundSheet}
        onClose={() => setShowRefundSheet(false)}
        amount={refundAmount}
      />
    </div>
  );
}

export default OrderStatus;
